
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import { scrollIntoViewIfNeeded } from '@/utils/helpers';
import { findSupportedLocale } from '@/utils/i18n-helpers';
import '@/validation-rules';

const DEFAULT_DELIVERY_INFORMATION = {
	type: 'school',
	extra: ''
};

@Component<DeliverySchool>({
	components: {
		ValidationProvider
	}
})
export default class DeliverySchool extends Vue {
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;
	@Prop({ type: Object, required: true, default: () => {} }) private restaurantDeliveryInfo!: SchoolDelivery;
	@Prop({ type: Object, required: false, default: () => {} }) private deliveryInformation!: CheckoutDeliveryInfo;

	private delivery: CheckoutDeliveryInfo = { ...DEFAULT_DELIVERY_INFORMATION };
	private schools: (string | undefined)[] | null = [];

	private get schoolDeliveryOptions(): { grades: string[] | null, classrooms: string[] | null, teachers: string[] | null } {
		const selectedSchool = this.restaurantDeliveryInfo.schools.length === 1
			// If there is only one school, return the fields for that school
			? this.restaurantDeliveryInfo.schools[0]
			// If there are multiple schools, return the fields for the selected school
			: this.delivery.school
				? this.restaurantDeliveryInfo.schools.find(school => school.name === this.delivery.school)
				: null;

		if (!selectedSchool) {
			return {
				grades: this.restaurantDeliveryInfo.schools.some(school => school.grades) ? [] : null,
				classrooms: this.restaurantDeliveryInfo.schools.some(school => school.classrooms) ? [] : null,
				teachers: this.restaurantDeliveryInfo.schools.some(school => school.teachers !== undefined) ? [] : null
			};
		}

		return {
			grades: selectedSchool.grades?.split(',') ?? null,
			classrooms: selectedSchool.classrooms?.split(',') ?? null,
			teachers: selectedSchool.teachers !== undefined
				? selectedSchool.teachers.length
					? selectedSchool.teachers.split(',')
					: []
				: null
		};
	}

	/**
	* Set schools in the selectors
	*
	* @return {void}
	*/
	private created(): void {
		this.schools = this.restaurantDeliveryInfo.schools.map((school) => school.name);
	}

	private mounted(): void {
		if(this.deliveryInformation) {
			this.delivery = this.deliveryInformation;
			this.delivery.type = 'school';
		}
		if(this.restaurantDeliveryInfo.schools.length === 1 && this.delivery.school === undefined) {
			this.delivery.school = this.restaurantDeliveryInfo.schools[0].name;
		}
	}

	/**
	 * Reset the grade, classroom and teacher fields when the school changes.
	 *
	 * @return {void}
	 */
	private onSchoolChange(): void {
		this.delivery.grade = '';
		this.delivery.classroom = '';
		this.delivery.teacher = '';
		this.updateDeliveryInfo();
	}

	/**
	* Send event to the parent to update the delivery
	* information in the vuex store
	*
	* @return {void}
	*/
	private updateDeliveryInfo(): void {
		// If the restaurant has locales, set address with localization
		if (this.restaurant.locales?.length && this.restaurant.locales.length > 1) {
			this.restaurant.locales.forEach((locale: RestaurantLocale) => {
				const supportedLocale: Locale | undefined = findSupportedLocale('', locale.locale_short);
				// Set the default locale as the address
				if (supportedLocale && locale.is_default) {
					this.delivery.address = this.buildAddress(supportedLocale.code);
				}
				// Set the address for the other supported locales
				else if (supportedLocale) {
					this.delivery.localization = {
						...this.delivery.localization,
						[locale.locale_short]: {
							address: this.buildAddress(supportedLocale.code)
						}
					};
				}
			});
		}
		// If no locale, just set the address
		else {
			this.delivery.address = this.buildAddress();
		}

		this.$emit('input', this.delivery);
	}

	/**
	 * Scroll to the bottom when opening a selector
	 *
	 * @param {string} elementId
	 * @return {void}
	 */
	private opened(elementId: string): void {
		setTimeout(() => {
			let contentDiv = document.querySelector(`#${elementId} .vs__dropdown-menu`);
			contentDiv && scrollIntoViewIfNeeded(contentDiv, { behavior: 'smooth', block: 'end', inline: 'start' });
		}, 100);
	}

	/**
	 * Build the address string for the delivery object, in the correct locale if applicable.
	 * Add each school delivery field together, depending on which fields are in the delivery object.
	 *
	 * @param {string} [localeCode]
	 * @return {string} Output example: 'School: Mushroom Kingdom, Grade: 1, Classroom: A, Teacher: Mario, Student: Luigi'
	 */
	private buildAddress(localeCode?: string): string {
		const schoolFields: (keyof CheckoutDeliveryInfo)[] = ['school', 'grade', 'classroom', 'teacher', 'student'];
		const address: string[] = [];

		schoolFields.forEach((field) => {
			const value = this.delivery[field];
			if (value) {
				const localizedAddress = localeCode
					? this.$t(`checkout.form.delivery.school.${field}_address`, localeCode, { [field]: value })
					: this.$t(`checkout.form.delivery.school.${field}_address`, { [field]: value });
				address.push(localizedAddress);
			}
		});

		return address.join(', ');
	}
}
